import React, { useState, useRef } from 'react';
import { Check, Upload, X } from 'lucide-react';
import { mediaService } from '../../services/mediaService';
import { MediaUploadOptions, UploadProgress } from '../../types/media';
import { MediaTypeIcon, mediaTone } from './MediaTypeIcon';

interface FileUploadProps {
  onUploadComplete?: (mediaAsset: any) => void;
  onUploadError?: (error: string) => void;
  options?: MediaUploadOptions;
  accept?: string;
  multiple?: boolean;
  className?: string;
  children?: React.ReactNode;
}

interface UploadingFile {
  file: File;
  progress: UploadProgress;
  error?: string;
  completed?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onUploadComplete,
  onUploadError,
  options = {},
  accept,
  multiple = false,
  className = '',
  children,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const newUploadingFiles: UploadingFile[] = fileArray.map(file => ({
      file,
      progress: { loaded: 0, total: file.size, percentage: 0 },
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    // Upload each file
    fileArray.forEach((file) => {
      const uploadOptions: MediaUploadOptions = {
        ...options,
        onProgress: (progress) => {
          setUploadingFiles(prev =>
            prev.map(uploadingFile =>
              uploadingFile.file === file
                ? { ...uploadingFile, progress }
                : uploadingFile
            )
          );
        },
      };

      mediaService.uploadFile(file, uploadOptions)
        .then((mediaAsset) => {
          setUploadingFiles(prev =>
            prev.map(uploadingFile =>
              uploadingFile.file === file
                ? { ...uploadingFile, completed: true }
                : uploadingFile
            )
          );
          onUploadComplete?.(mediaAsset);
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
          setUploadingFiles(prev =>
            prev.map(uploadingFile =>
              uploadingFile.file === file
                ? { ...uploadingFile, error: errorMessage }
                : uploadingFile
            )
          );
          onUploadError?.(errorMessage);
        });
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value to allow uploading the same file again
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeUploadingFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(uploadingFile => uploadingFile.file !== file));
  };

  const clearCompleted = () => {
    setUploadingFiles(prev => prev.filter(uploadingFile => !uploadingFile.completed));
  };

  const hasCompleted = uploadingFiles.some(f => f.completed);

  return (
    <div className={`file-upload font-ui ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />

      <div
        role="button"
        tabIndex={0}
        title={accept ? `Accepted: ${accept}` : 'All file types'}
        className={`flex min-h-[64px] cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed px-4 py-4 text-center text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          isDragOver
            ? 'border-primary-600/60 bg-primary-50/60 text-primary-700'
            : 'border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:bg-neutral-50 hover:text-ink'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children || (
          <>
            <Upload className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            <span>Drop files or click to upload</span>
          </>
        )}
      </div>

      {uploadingFiles.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 flex h-7 items-center justify-between">
            <span className="text-[12px] text-neutral-400">Uploads</span>
            {hasCompleted && (
              <button
                type="button"
                onClick={clearCompleted}
                className="rounded-full px-2 text-[12px] text-neutral-500 transition-colors hover:text-ink"
              >
                Clear done
              </button>
            )}
          </div>

          <ul className="divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            {uploadingFiles.map((uploadingFile, index) => {
              const { file, progress, error, completed } = uploadingFile;
              return (
                <li key={index} className="flex items-center gap-3 px-3 py-2">
                  <MediaTypeIcon mimeType={file.type} className={`h-4 w-4 flex-shrink-0 ${mediaTone(file.type).text}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-[13px] text-ink">{file.name}</span>
                      {error ? (
                        <span className="flex-shrink-0 text-[12px] text-primary-700">Failed</span>
                      ) : completed ? (
                        <span className="inline-flex flex-shrink-0 items-center gap-1 text-[12px] text-neutral-500">
                          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                          Done
                        </span>
                      ) : (
                        <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
                          {progress.percentage}%
                        </span>
                      )}
                    </div>
                    {error ? (
                      <p className="mt-0.5 truncate text-[12px] text-primary-700" title={error}>
                        {error}
                      </p>
                    ) : !completed ? (
                      <div
                        className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-neutral-100"
                        title={`${mediaService.formatFileSize(progress.loaded)} / ${mediaService.formatFileSize(progress.total)}`}
                      >
                        <div
                          className="h-full rounded-full bg-ink transition-all duration-300"
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeUploadingFile(file)}
                    aria-label="Remove"
                    title="Remove"
                    className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
